import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {TableColumn, TableSettings} from "../../../../tableSettings";
import AbstractListView, {
  DataFetchResult,
  ExportRowRenderer,
  ListCustomFilterRenderer,
  ListOrderBy,
  ListRowRenderer
} from "../../../common/AbstractListView";

import constants from '../../../../constants';
import {Search} from "../../../../filtering";

import {NavigationType, openNewPage} from '../../../../util/navigationHelpers';

import {
  DateRenderer,
  DateTimeRenderer,
  FloatRenderer,
  NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,
  TextRenderer
} from '../../../../uiControls/SmartTableRenderers';
import ApplicationContext from '../../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../../common/JobListViewContext';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../../ui/ActionButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import {JobNames} from "../../../../framework.core/JobNames";
import {payrollSummaryList} from "../../tableSettings";
import ListViewShortcutMenu from '../../../common/ListViewShortcutMenu';
import { PayrollSummaryListItem } from '../../../../fms.data/models/PayrollSummaryListItem';
import * as XCol from '../../../../framework.core/XExportColumn';
import ToolboxMenuItem from '../../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../../ui/menu/Menu';
import JobListSuntekCommand from '../../../common/JobListSuntekCommand';
import CheckBoxUncontrolled from '../../../../ui/CheckBoxUncontrolled';
import CheckBoxIcon from '../../../../ui/CheckBoxIcon';
import Label from '../../../../ui/Label';
import { FmsSecTask } from '../../../../fms.core/constants';
import { SuntekSettings, SystemSettings } from '../../../../ifmsTypes';
import { findSecurityTask, findSecurityTaskWithBranch, getSecBySecTask } from '../../../../util';
import { useJournal } from '../journal/JournalListView';
import useSuntekSettings from '../../../../hooks/useSuntekSettings';
import { nullableCheckBoxValue } from '../../../common/commonExportFormat';
import IconButton, { JournalIconButton, ViewIconButton, ViewIconButton2 } from '../../../../ui/IconButton';
import { SecType } from '../../../../fms.enum';

function PaidFilterRenderer({value, onChange}: any) {
  return (
    <div>
      <CheckBoxUncontrolled name='ifms-filter-bool-search-check' initialValue={value} onChange={e => onChange(e)} />
      <Label style={{marginLeft: '5px'}} label="Paid" />
    </div>
  );
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();
  const operation = 'accounting';

  const selectedElement = jobListContext.selectedElement as PayrollSummaryListItem;

  async function deleteEntity() {
    if (!selectedElement) {
      return;
    }
    if (selectedElement.void || selectedElement.fedTaxPaid || selectedElement.stateTaxPaid || selectedElement.netSalaryPaid || selectedElement.addTaxPaid || selectedElement.addPayPaid) {
      return;
      }
    const id = selectedElement.id;
    openNewPage(history, `/${operation}/acPayrollSummary/delete/${id}`, NavigationType.newPage);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const viewUrl = `/${operation}/acPayrollSummary/view/${id}`;

    const urls = [viewUrl];

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  return { deleteEntity, view, operation };
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { deleteEntity, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as PayrollSummaryListItem;
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTaskWithBranch(appContext, FmsSecTask.AcctPayroll, selectedElement?.branch);

  const items = [
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantRead && !listSec?.grantDel },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel },
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true, newCol: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="payroll-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        view();
        break;
      case 1:
        deleteEntity();
        break;
      case 2:
        jobListContext.triggerTableSearch('F5');
        break;
      case 3:
        jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}
// 250806.inhyung. Payroll(Old)에도 payroll2와 같은 렌더러 적용.
function CheckboxIconRenderer({data, row, cell}: any) {
  const l = data[row.index];

  if (cell && cell.value) {
    return (
      <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default" data-tooltip="Paid">
        <CheckBoxIcon value={true} />
      </div>);  
  } else {
    let amount = 0;
    switch(cell.column.dataIndex) {
      case "netPayPaid":
        amount = l.netPayAmt;
        break;
      case "fedTaxPaid":
        amount = l.fedTaxAmt;
        break;
      case "fedUnemployPaid":
        amount = l.fedUnemployAmt;
        break;
      case "stateTaxPaid":
        amount = l.stateTaxAmt;
        break;
      case "stateUnemployPaid":
        amount = l.stateUnemployAmt;
        break;
    }

    if (amount === 0) {
      return (
        <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default">x</div>
      );
    } else {
      return (
        <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default" data-tooltip="Not Paid">
          <CheckBoxIcon value={true} color="red" />
        </div>
      ); 
    }
  }
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);

  const selectedElement = jobListContext.selectedElement as PayrollSummaryListItem;

  const {view} = useSelectedOperations();
  
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPayroll);

  const hasSelectedRow = !!selectedElement;

  const canBeRead = listSec?.grantRead;

  return (
    <div className='ifms-common-no-vertical-separator-gap-margin'>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
      <div className='ifms-common-vertical-gap-8'>
        <ViewIconButton2 name='payroll-list-view-button' disabled={!hasSelectedRow || !canBeRead} onClick={view} useText iconHeight='26px' />
       </div>
      {/* <PrimaryButton disabled={!canBeDeleted} onClick={deleteEntity}>Void</PrimaryButton> */}
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as PayrollSummaryListItem;
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPayroll);

  const {view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {

    } else if (unprocessedFkey === "F9") {
      if (listSec?.grantRead)
        view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F2', 'F3', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

function formatData(data:Array<PayrollSummaryListItem>) {
  return data.map(d => ({
    ...d,
  }));
}

function TrancationImpactButtonCommands({ userId } : {userId: string}) {
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as PayrollSummaryListItem;
  const {switchJournal} = useJournal();
  const suntekSettings = useSuntekSettings();
  const appContext = useContext(ApplicationContext);
  
  if (!getSecBySecTask(appContext, FmsSecTask.ListJournalBillings, SecType.Read) && !userId.startsWith('@')) {
    return null;
  }

  return (
    <JournalIconButton onClick={() => switchJournal(JobNames.PayrollSummary, selectedElement.id)} useText/>
  );
}

/**
 * Concrete implementation for Payroll Summary List screen
 *
 * @class PayrollSummaryListView
 * @extends {AbstractListView}
 */
class PayrollSummaryListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.PayrollSummary,
      'PayrollSummaryListItem',
      PayrollSummaryListItem.calculateSearchParameters(search,PayrollSummaryListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  
  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.PayrollSummary, 'PayrollSummaryListItem',
      PayrollSummaryListItem.calculateSearchParameters(search,PayrollSummaryListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  formatData(apiResult.entities);
  }

  

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'netPayPaid',
        renderer: PaidFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v.value ?? v,
      },
      {
        accessor: 'fedTaxPaid',
        renderer: PaidFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v.value ?? v,
      },
      {
        accessor: 'fedUnemployPaid',
        renderer: PaidFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v.value ?? v,
      },
      {
        accessor: 'stateTaxPaid',
        renderer: PaidFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v.value ?? v,
      },
      {
        accessor: 'stateUnemployPaid',
        renderer: PaidFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v.value ?? v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;  // ??
  }

  getRowRenderers(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): ListRowRenderer[] {
    const useBranch = systemSettings?.enableBranch ?? false;
    return PayrollSummaryListItem.getExportCollection(useBranch).map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.dataIndex === 'netPayPaid') colRenderer.Cell = CheckboxIconRenderer;
      else if(x.dataIndex === 'fedTaxPaid') colRenderer.Cell = CheckboxIconRenderer;
      else if(x.dataIndex === 'fedUnemployPaid') colRenderer.Cell = CheckboxIconRenderer;
      else if(x.dataIndex === 'stateTaxPaid') colRenderer.Cell = CheckboxIconRenderer;
      else if(x.dataIndex === 'stateUnemployPaid') colRenderer.Cell = CheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props:any) => <FloatRenderer {...props} decimals={2} />;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = CheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      return colRenderer;
    });
  }

  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({ accessor: 'status', changedValue: (value: any) => { return value['status'] === 'PC' ? 'Period Close' : '' } });
    colRenderer.push({ accessor: 'netPayPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['netPayPaid']) } });
    colRenderer.push({ accessor: 'fedTaxPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['fedTaxPaid']) } });
    colRenderer.push({ accessor: 'fedUnemployPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['fedUnemployPaid']) } });
    colRenderer.push({ accessor: 'stateTaxPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['stateTaxPaid']) } });
    colRenderer.push({ accessor: 'stateUnemployPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['stateUnemployPaid']) } });
    colRenderer.push({ accessor: 'netSalaryPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['netSalaryPaid']) } });
    colRenderer.push({ accessor: 'addPayPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['addPayPaid']) } });
    colRenderer.push({ accessor: 'addTaxPaid', changedValue: (value: any) => { return nullableCheckBoxValue(value['addTaxPaid']) } });
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): TableSettings {
    const tsettings =  payrollSummaryList;
    const useBranch = systemSettings?.enableBranch ?? false;
    const cols = PayrollSummaryListItem.getExportCollection(useBranch);
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: PayrollSummaryListItem) {
    return row && row.id ? `/accounting/acPayrollSummary/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Payroll (Old) List';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.PayrollSummary} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }

  transactionImpactBtn(userId: string): React.ReactNode {
    return <TrancationImpactButtonCommands userId={userId} />
  }
}

export default PayrollSummaryListView;
